import { JobToast, LandReportViewProvider, PaginatedFieldsProvider } from '@cibo/landmanager'
import {
  AUTH_ROUTES,
  AuthLayout,
  AuthUserPermission,
  Can,
  Loading,
  Preferences,
  TermsDisplay,
  useAuth,
} from '@cibo/profile'
import { FullScreenError } from '@cibo/ui'
import { Box, Divider } from '@mui/material'
import { AdminLayout } from 'components/AdminLayout'
import { ImpactLayout, LayoutSection } from 'components/ImpactLayout'
import { LearnArticleList } from 'components/LearnArticleList'
import { UsersLayout } from 'components/UsersLayout'
import { Workflow } from 'features/Workflows'
import { StatusCodes } from 'http-status-codes'
import 'mapbox-gl/dist/mapbox-gl.css'
import { ADMIN_PATHS, ADMIN_ROUTES, ROUTE } from 'navigation/types'
import {
  CARBON_LAB_ROUTES,
  ENROLLMENT_PATHS,
  LAND_MANAGER_PATHS,
  LAND_MANAGER_ROUTES,
  PROGRAM_PATHS,
  PROGRAM_ROUTES,
  SCALED_ANALYSIS_ROUTES,
} from 'navigation/types/routes'
import LogoutPage from 'pages/Account/LogoutPage'
import { AdminRoot } from 'pages/Admin'
import { AgreementsOverview, AgreementsOverviewRedirect } from 'pages/Admin/Agreements'
import {
  OrganizationDetail,
  OrganizationPrograms,
  OrganizationUser,
  OrganizationUsers,
} from 'pages/Admin/OrganizationDetail'
import { OrganizationList } from 'pages/Admin/OrganizationList'
import { ProgramDetail, ProgramList, ProgramOrganizationDetail } from 'pages/Admin/Programs'
import { AdminProgramDetailLayout } from 'pages/Admin/Programs/AdminProgramDetailLayout'
import { AgreementDetail } from 'pages/Agreements'
import { CommitProgram } from 'pages/CommitProgram'
import { ControlGroups, ControlGroupsDetail } from 'pages/ControlGroups'
import { EditLand } from 'pages/EditLand'
import { EditLandDetails } from 'pages/EditLandDetails'
import { Enrollments, EnrollmentsTable } from 'pages/Enrollments'
import { CreateEnrollment } from 'pages/Enrollments/CreateEnrollment'
import { Home, HomeLayout } from 'pages/Home'
import { OnboardingDisplayStateProvider } from 'pages/Home/useOnboardingDisplayState'
import { BoundaryEditor, Farms, LandManager } from 'pages/LandManager'
import { AddFields } from 'pages/LandManager/Add'
import { LandManagerDrawFields } from 'pages/LandManager/Draw'
import { FieldMapPage } from 'pages/LandManager/FieldMap'
import { LandReport } from 'pages/LandManager/LandReport'
import { LandManagerMapView } from 'pages/LandManager/MapView'
import { OpportunitiesRedirect } from 'pages/Opportunities'
import { ProfileFields } from 'pages/ProfileFields'
import { ProgramsRoot } from 'pages/Programs'
import { ProgramAbout } from 'pages/Programs/ProgramAbout'
import { ProgramDetailLayout } from 'pages/Programs/ProgramDetail/ProgramDetailLayout'
import { EligibleFields } from 'pages/Programs/ProgramDetail/ProgramV1Detail/EligibleFields'
import { EnrolledFields } from 'pages/Programs/ProgramDetail/ProgramV1Detail/EnrolledFields'
import { ProgramEnrollments } from 'pages/Programs/ProgramDetail/ProgramV1Detail/Enrollments'
import { ProgramRedirects } from 'pages/Programs/ProgramRedirects'
import { LandManagerUploadFlow } from 'pages/UploadFlow'
import { UserList } from 'pages/UserList'
import {
  UserAgreements,
  UserFields,
  UserLayout,
  UserProfile,
  UserPrograms,
} from 'pages/UserProfile'
import { CreateUser } from 'pages/UserProfile/Create'
import { Suspense, lazy } from 'react'
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom'
import {
  ProgramEngagementPlayer,
  ProgramEngagementStepStatus,
} from './components/ProgramEngagementPlayer'
import { ENGAGEMENT_PATHS } from './navigation/types/engagementRoutes'
import { GROWERS_PATHS } from './navigation/types/growersRoutes'
import { SCOPE_THREE_PATHS } from './navigation/types/scopeThreeRoutes'
import { ProgramEnrollmentMap } from './pages/Admin/Programs/ProgramEnrollmentMap'
import { EngagementsRoot } from './pages/Engagements'
import { GrowersIndex } from './pages/Growers'
import { GrowerDrawField, GrowerUploadFlow } from './pages/Growers/Add'
import { GrowerEdit, GrowersDetail } from './pages/Growers/Detail'
import { GrowersProgramDetail } from './pages/Growers/Detail/ProgramDetail'
import { GrowersLayout } from './pages/Growers/Layout'
import { ProgramLayout } from './pages/Growers/ProgramLayout'
import { AddInventory, InventoriesIndex } from './pages/Inventories'
import { BoundaryTab } from './pages/Inventories/Boundary'
import { InventoryDashboard } from './pages/Inventories/Dashboard'
import { HistoricalOfftake } from './pages/Inventories/HistoricalOfftake'
import { InventorySetup, InventoryTargets } from './pages/Inventories/Setup'
import { Engagements } from './pages/Programs/ProgramDetail/ProgramV2Detail/Engagements'
import { AdminDrawField, AdminUploadFlow } from './pages/UserProfile/Add'
import { UserNavigation } from './pages/UserProfile/UserNavigation'

const AccountPage = lazy(() => import('pages/Account'))
const LazyAccountPage = () => {
  return (
    <Suspense fallback={<Loading large />}>
      <AccountPage />
    </Suspense>
  )
}
const ScaledAnalysis = lazy(() => import('features/ScaledAnalysis'))
const LazyScaledAnalysis = () => {
  return (
    <Suspense fallback={<Loading large />}>
      <ScaledAnalysis />
    </Suspense>
  )
}

const HIDE_FOOTER_ROUTES = [LAND_MANAGER_ROUTES.MAP, LAND_MANAGER_ROUTES.DETAIL_MAP]

export const LoggedInRoutes = () => {
  const { persona } = useAuth()

  const { pathname } = useLocation()

  return (
    <OnboardingDisplayStateProvider>
      {/* Below places the notistack snackbar below the menu bar */}
      <style>{`.SnackbarContainer-top { top: 60px !important; }`}</style>
      <Routes>
        <Route
          path={ROUTE.TERMS_OF_USE}
          element={
            <AuthLayout variant="content">
              <TermsDisplay />
            </AuthLayout>
          }
        />
        <Route
          path={ROUTE.PRIVACY_POLICY}
          element={
            <AuthLayout variant="content">
              <TermsDisplay />
            </AuthLayout>
          }
        />
        <Route
          path="/"
          element={
            <ImpactLayout hideFooter={HIDE_FOOTER_ROUTES.includes(pathname)}>
              <Outlet />
            </ImpactLayout>
          }
        >
          <Route path={`${ROUTE.ACCOUNT}/*`} element={<LazyAccountPage />} />
          <Route path={ADMIN_ROUTES.ROOT} element={<AdminLayout />}>
            <Route path={ADMIN_PATHS.USERS} element={<UsersLayout />}>
              <Route path={ADMIN_PATHS.CREATE} element={<CreateUser />} />
              <Route
                path={ADMIN_PATHS.USER_ID}
                element={
                  <UserLayout>
                    <UserNavigation />
                  </UserLayout>
                }
              >
                <Route path={ADMIN_PATHS.USER_FIELDS}>
                  <Route path={ADMIN_PATHS.USER_FIELDS_ADD}>
                    <Route path={ADMIN_PATHS.USER_FIELDS_DRAW} element={<AdminDrawField />} />
                    <Route path={ADMIN_PATHS.USER_FIELDS_UPLOAD} element={<AdminUploadFlow />} />
                  </Route>
                  <Route
                    index
                    element={
                      <PaginatedFieldsProvider>
                        <UserFields />
                      </PaginatedFieldsProvider>
                    }
                  />
                </Route>
                <Route path={ADMIN_PATHS.PROGRAM_ACTIVITY} element={<UserPrograms />} />
                <Route path={ADMIN_PATHS.PROFILE} element={<UserProfile />} />
                <Route path={ADMIN_PATHS.PREFERENCES} element={<Preferences />} />
                <Route path={ADMIN_PATHS.AGREEMENTS}>
                  <Route
                    path={ADMIN_PATHS.AGREEMENT_ID}
                    element={<AgreementDetail variant="user" />}
                  />
                  <Route index element={<UserAgreements />} />
                </Route>
                <Route index element={<UserPrograms />} />
              </Route>
              <Route index element={<UserList />} />
            </Route>
            <Route path={ADMIN_PATHS.ORGANIZATIONS}>
              <Route path={ADMIN_PATHS.ORGANIZATION_ID} element={<OrganizationDetail />}>
                <Route index element={<OrganizationUsers />} />
                <Route path={ADMIN_PATHS.USERS}>
                  <Route path={ADMIN_PATHS.USER_ID} element={<OrganizationUser />} />
                  <Route index element={<OrganizationUsers />} />
                </Route>
                <Route path={ADMIN_PATHS.PROGRAMS} element={<OrganizationPrograms />} />
              </Route>
              <Route index element={<OrganizationList />} />
            </Route>

            {/* Agreements Module */}
            <Route
              path={ADMIN_PATHS.GROWER_MANAGEMENT}
              element={
                <Can useAny={[AuthUserPermission.MANAGE_AGREEMENTS]} redirectPath="/">
                  <LayoutSection>
                    <Outlet />
                  </LayoutSection>
                </Can>
              }
            >
              <Route path={ADMIN_PATHS.AGREEMENT_TYPE}>
                <Route
                  path={ADMIN_PATHS.AGREEMENT_ID}
                  element={<AgreementDetail variant="agreements" />}
                />
                <Route index element={<AgreementsOverview />} />
              </Route>
              <Route index element={<AgreementsOverviewRedirect />} />
            </Route>

            {/* Programs Module */}
            <Route path={ADMIN_PATHS.PROGRAMS}>
              <Route path={ADMIN_PATHS.PROGRAM_ID} element={<AdminProgramDetailLayout />}>
                <Route path={ADMIN_PATHS.ORGANIZATION_ID} element={<ProgramOrganizationDetail />} />
                <Route index element={<ProgramDetail />} />
              </Route>
              <Route path={ADMIN_PATHS.MAP} element={<ProgramEnrollmentMap />} />
              <Route index element={<ProgramList />} />
            </Route>

            {/* Enrollments Module */}
            <Route
              path={ADMIN_PATHS.ENROLLMENTS}
              element={
                <LayoutSection sx={{ flex: 1 }}>
                  <EnrollmentsTable />
                </LayoutSection>
              }
            />
            <Route index element={<AdminRoot />} />
          </Route>
          <Route path={`${ROUTE.HOME}/*`} element={<Home />} />
          {/* START Field management */}
          <Route
            path={LAND_MANAGER_ROUTES.ROOT}
            element={
              <LandReportViewProvider>
                {persona === 'grower' ? <HomeLayout /> : <Outlet />}
              </LandReportViewProvider>
            }
          >
            <Route path={LAND_MANAGER_PATHS.ADD}>
              <Route path={LAND_MANAGER_PATHS.UPLOAD}>
                <Route index element={<LandManagerUploadFlow />} />
                <Route path={LAND_MANAGER_PATHS.DETAILS} element={<EditLandDetails />} />
              </Route>
              <Route path={LAND_MANAGER_PATHS.DRAW} element={<LandManagerDrawFields />} />
              <Route
                index
                element={
                  <AddFields
                    paths={{ draw: LAND_MANAGER_ROUTES.DRAW, upload: LAND_MANAGER_ROUTES.UPLOAD }}
                  />
                }
              />
            </Route>
            <Route path={LAND_MANAGER_PATHS.PERSONAL} element={<LandManager />} />
            <Route path={LAND_MANAGER_PATHS.ARCHIVED} element={<LandManager />} />
            <Route path={LAND_MANAGER_PATHS.MAP} element={<LandManagerMapView />} />
            <Route path={LAND_MANAGER_PATHS.DETAIL}>
              <Route path={LAND_MANAGER_PATHS.EDIT} element={<EditLand />} />
              <Route path={LAND_MANAGER_PATHS.MAP} element={<FieldMapPage />} />
              <Route path={LAND_MANAGER_PATHS.EDIT_BOUNDARY} element={<BoundaryEditor />} />
              <Route index element={<LandReport />} />
            </Route>
            <Route index element={<LandManager />} />
          </Route>
          <Route
            path={LAND_MANAGER_ROUTES.FARMS}
            element={
              <Can useAny={[AuthUserPermission.FARMS_MANAGE]} redirectPath="/">
                <Farms />
              </Can>
            }
          />
          {/* END Field management */}
          <Route path={`${SCALED_ANALYSIS_ROUTES.ROOT}/*`} element={<LazyScaledAnalysis />} />
          <Route path={ROUTE.ACCOUNT_LOGOUT} element={<LogoutPage />} />
          <Route path={AUTH_ROUTES.LOGIN} element={<Navigate replace to="/" />} />
          <Route path={AUTH_ROUTES.REGISTRATION} element={<Navigate replace to="/" />} />
          <Route path={AUTH_ROUTES.FORGOT_PASSWORD} element={<Navigate replace to="/" />} />
          <Route path={AUTH_ROUTES.EMULATE_USER} element={<Navigate replace to="/" />} />
          <Route path={AUTH_ROUTES.TERMS} element={<Navigate replace to="/" />} />
          <Route path={ROUTE.PROFILE_FIELD} element={<ProfileFields />} />
          <Route path={ROUTE.PROFILE_FIELDS} element={<ProfileFields />} />
          <Route path={ROUTE.OPPORTUNITIES} element={<OpportunitiesRedirect />} />
          <Route path="/" element={<HomeLayout />}>
            {/* START Programs */}
            <Route path={PROGRAM_ROUTES.ROOT}>
              <Route path={PROGRAM_PATHS.DETAIL} element={<ProgramDetailLayout />}>
                {/* No idea why I had to add this route here. Routing for V2 program 
                details should be handled at the <ProgramV2DetailLayout /> level */}
                <Route path={PROGRAM_PATHS.ENGAGEMENTS} element={<Engagements />} />
                <Route path={PROGRAM_PATHS.ENROLLMENTS} element={<ProgramEnrollments />} />
                <Route path={PROGRAM_PATHS.ELIGIBLE} element={<EligibleFields />} />
                <Route path={PROGRAM_PATHS.ABOUT} element={<ProgramAbout />} />
                <Route index element={<EnrolledFields />} />
              </Route>
              <Route path={PROGRAM_PATHS.DETAIL}>
                <Route path={PROGRAM_PATHS.ENROLL}>
                  <Route path={PROGRAM_PATHS.ENROLL_DETAIL} element={<Workflow />} />
                  <Route index element={<ProgramRedirects />} />
                </Route>
                <Route path={ENROLLMENT_PATHS.COMMIT} element={<CommitProgram />} />
                <Route path={PROGRAM_PATHS.USER} element={<>user program detail</>} />
              </Route>
              <Route path={PROGRAM_PATHS.DETAIL}>
                <Route path={PROGRAM_PATHS.ENGAGEMENTS} element={<EngagementsRoot />}>
                  <Route path={ENGAGEMENT_PATHS.ENGAGEMENT}>
                    <Route path={ENGAGEMENT_PATHS.STAGE}>
                      <Route path={ENGAGEMENT_PATHS.STEP}>
                        <Route
                          path={ENGAGEMENT_PATHS.STATUS}
                          element={<ProgramEngagementStepStatus />}
                        />
                        <Route path={ENGAGEMENT_PATHS.TASK} element={<ProgramEngagementPlayer />} />
                        <Route index element={<ProgramEngagementPlayer />} />
                      </Route>
                      <Route index element={<ProgramEngagementPlayer />} />
                    </Route>
                    <Route index element={<ProgramEngagementPlayer />} />
                  </Route>
                </Route>
              </Route>
              <Route index element={<ProgramsRoot />} />
            </Route>

            <Route path={GROWERS_PATHS.ROOT} element={<GrowersLayout />}>
              <Route path={GROWERS_PATHS.DETAIL}>
                <Route path={GROWERS_PATHS.EDIT} element={<GrowerEdit />} />
                <Route path={GROWERS_PATHS.PROGRAM} element={<ProgramLayout />}>
                  <Route path={ENGAGEMENT_PATHS.ENGAGEMENT}>
                    <Route path={ENGAGEMENT_PATHS.STAGE}>
                      <Route path={ENGAGEMENT_PATHS.STEP}>
                        <Route
                          path={ENGAGEMENT_PATHS.STATUS}
                          element={<ProgramEngagementStepStatus />}
                        />
                        <Route path={ENGAGEMENT_PATHS.TASK} element={<ProgramEngagementPlayer />} />

                        <Route index element={<ProgramEngagementPlayer />} />
                      </Route>
                      <Route index element={<ProgramEngagementPlayer />} />
                    </Route>
                    <Route index element={<ProgramEngagementPlayer />} />
                  </Route>
                  <Route index element={<GrowersProgramDetail />} />
                </Route>
                <Route path={GROWERS_PATHS.ADD_FIELD}>
                  <Route path={GROWERS_PATHS.DRAW} element={<GrowerDrawField />} />
                  <Route path={GROWERS_PATHS.UPLOAD} element={<GrowerUploadFlow />} />
                </Route>
                <Route index element={<GrowersDetail />} />
              </Route>
              <Route index element={<GrowersIndex />} />
            </Route>

            {/* Deprecated routes */}
            <Route path="/programs/workflow/:workflowId" element={<ProgramRedirects />} />
            {/* END Programs */}

            <Route path={ENROLLMENT_PATHS.ROOT}>
              <Route
                path={ENROLLMENT_PATHS.CREATE}
                element={
                  <PaginatedFieldsProvider>
                    <CreateEnrollment />
                  </PaginatedFieldsProvider>
                }
              />

              <Route path={ENROLLMENT_PATHS.PERSONAL}>
                <Route index element={<Enrollments />} />
                <Route
                  path={ENROLLMENT_PATHS.CREATE}
                  element={
                    <PaginatedFieldsProvider>
                      <CreateEnrollment personal />
                    </PaginatedFieldsProvider>
                  }
                />
              </Route>
              <Route path={ENROLLMENT_PATHS.DETAIL} element={<Workflow />} />
              <Route index element={<Enrollments />} />
            </Route>

            <Route
              path={ROUTE.LEARN}
              element={
                <LayoutSection container>
                  <Divider />
                  <Box>
                    <LearnArticleList />
                  </Box>
                </LayoutSection>
              }
            />
            <Route
              path={ROUTE.INVENTORIES}
              element={
                <Can
                  useAny={[
                    AuthUserPermission.SCOPE3_INVENTORY_CREATE,
                    AuthUserPermission.SCOPE3_INVENTORY_WRITE,
                    AuthUserPermission.SCOPE3_INVENTORY_READ,
                  ]}
                  redirectPath="/"
                >
                  <InventoriesIndex />
                </Can>
              }
            >
              <Route path={SCOPE_THREE_PATHS.CREATE} element={<AddInventory />} />
              <Route path={SCOPE_THREE_PATHS.INVENTORY} element={<Outlet />}>
                <Route path={SCOPE_THREE_PATHS.SETUP} element={<InventorySetup />} />
                <Route path={SCOPE_THREE_PATHS.TARGETS} element={<InventoryTargets />} />
                <Route path={SCOPE_THREE_PATHS.BOUNDARY} element={<BoundaryTab />} />
                <Route path={SCOPE_THREE_PATHS.OFFTAKE} element={<HistoricalOfftake />} />
                <Route path={SCOPE_THREE_PATHS.DASHBOARD} element={<InventoryDashboard />} />
                <Route index element={<InventoryDashboard />} />
              </Route>
            </Route>

            <Route index element={<Home />} />
          </Route>

          <Route path={ROUTE.CONTROL_GROUPS} element={<ControlGroups />} />
          <Route path={ROUTE.CONTROL_GROUPS_DETAIL} element={<ControlGroupsDetail />} />

          <Route path="*" element={<Outlet />}>
            <Route path="*" element={<FullScreenError status={StatusCodes.NOT_FOUND} />} />
          </Route>
        </Route>
      </Routes>

      <JobToast
        suppressPaths={[CARBON_LAB_ROUTES.JOB_ID]}
        CARBON_LAB_ROUTES={{
          CARBON_LAB_JOB: CARBON_LAB_ROUTES.JOB_ID,
          LAND_DETAIL: LAND_MANAGER_ROUTES.DETAIL,
          LAND_DETAIL_SUBROUTE_CARBON_LAB: LAND_MANAGER_ROUTES.DETAIL_CARBON_LAB,
        }}
      />
    </OnboardingDisplayStateProvider>
  )
}
